import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import type { SchemaIssueLike } from '../types/forms';
import { isApiError } from './api/api-error';

/**
 * Aplica os erros de campo de um VALIDATION_ERROR da API ao formulário.
 * `fieldMap` traduz caminhos da API para nomes de campos do formulário (ex.: startAt → startTime).
 * Retorna `true` se ao menos um erro foi associado a um campo.
 */
export function applyApiErrorToForm<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fieldMap: Record<string, string> = {},
): boolean {
  if (!isApiError(error)) return false;
  const fieldErrors = error.fieldErrors;
  let applied = false;
  for (const fe of fieldErrors) {
    const path = fieldMap[fe.path] ?? fe.path;
    if (!path) continue;
    setError(path as Path<T>, { type: 'server', message: fe.message });
    applied = true;
  }
  return applied;
}

/** Aplica issues de um `safeParse` (schemas compartilhados) ao formulário. */
export function applySchemaIssuesToForm<T extends FieldValues>(
  issues: readonly SchemaIssueLike[],
  setError: UseFormSetError<T>,
  fieldMap: Record<string, string> = {},
): void {
  const seen = new Set<string>();
  for (const issue of issues) {
    const raw = issue.path.map(String).join('.');
    const path = fieldMap[raw] ?? raw;
    if (!path || seen.has(path)) continue;
    seen.add(path);
    setError(path as Path<T>, { type: 'schema', message: issue.message });
  }
}

/** Converte string vazia em null (para selects/inputs opcionais). */
export function emptyToNull(value: string | null | undefined): string | null {
  return value && value.trim() ? value.trim() : null;
}
